import type { ModelBox } from '@vrlearning/lesson-schema';

/**
 * The console's window onto a library model (ADR-043, contract C10): the
 * headset app at `?modelViewer=<ticket>`, embedded by the console's model page
 * as the furniture viewer is (ADR-037). No XR, no socket, no pairing; the
 * ticket -- single use, a minute -- fetches the model's facts, the clips its
 * GLB has and its signed URL. It shows the model at real size on a grey floor
 * beside a 1.5 m figure, from its front, turnable, and answers the console by
 * postMessage. The thumbnail is the model alone, without the figure or the
 * floor, framed to fill the picture (ADR-060):
 *
 * | Console → viewer | Viewer → console |
 * | --- | --- |
 * | `{type: 'play', clip: string \| null}`: a GLB clip by name; null, the bind pose | – |
 * | `{type: 'capture'}` | `{type: 'thumbnail', jpeg: Blob}` 640 × 480, JPEG q 0.85 |
 * | – | `{type: 'ready', clips: string[]}` once loaded; `{type: 'trouble', text}` in Dutch |
 *
 * It listens only to the console origin the ticket's answer names, and posts
 * only to it. The thumbnail is drawn here, in the admin's browser (the worker
 * has no GPU, spec default 12); the console uploads it.
 */

/** The model as the viewer needs it: lenient, as a model never processed to the end may be shown. */
export type ViewerModel = {
  readonly media: string;
  readonly name: string;
  /** Metres in the model frame; absent, measured from the GLB. */
  readonly box: ModelBox | undefined;
  /** Degrees, where the GLB's head points (0 = −z). */
  readonly forwardYaw: number;
};

/** What `GET /v1/model-viewer/{ticket}` answers. */
export type ModelViewerAnswer = {
  readonly model: ViewerModel;
  readonly clips: readonly string[];
  readonly media: Readonly<Record<string, string>>;
  readonly consoleOrigin: string;
};

/** The admin's words, in Dutch and formal. */
export const MODEL_VIEWER_TEXT = {
  loading: 'Het model wordt geladen…',
  gone: 'Deze voorvertoning is verlopen. Open het model opnieuw.',
  failed: 'Het model kon niet worden geladen.',
  noClip: 'Deze animatie zit niet in het bestand.',
  drawing: 'Het plaatje lukt niet in deze browser.',
} as const;

/** The thumbnail: 640 × 480, JPEG at 0.85. */
export const MODEL_THUMBNAIL = { width: 640, height: 480, quality: 0.85 } as const;

/** Reads `?modelViewer=<ticket>` and takes it off the address at once (a used ticket is worthless). */
export function takeModelViewerRequest(
  place: { href: string } | undefined = globalThis.location,
  history: Pick<History, 'replaceState'> | undefined = globalThis.history,
): { ticket: string } | undefined {
  let url: URL;
  try {
    url = new URL(place?.href ?? '');
  } catch {
    return undefined;
  }
  const ticket = url.searchParams.get('modelViewer');
  if (!ticket) return undefined;
  history?.replaceState(null, '', url.pathname);
  return { ticket };
}

export type ModelViewerOutcome =
  | { kind: 'ready'; answer: ModelViewerAnswer }
  | { kind: 'gone' }
  | { kind: 'failed'; detail: string };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const positive = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

/** An origin and nothing else (`https://host:port`): postMessage's target is never `*` or a URL. */
function isOrigin(value: string): boolean {
  try {
    const url = new URL(value);
    return url.origin !== 'null' && url.origin === value;
  } catch {
    return false;
  }
}

/** The model's facts as far as the viewer needs them; undefined without a GLB to show. */
export function viewerModelOf(value: unknown): ViewerModel | undefined {
  if (!isRecord(value) || typeof value['media'] !== 'string') return undefined;
  const box = value['box'];
  const yaw = value['forwardYaw'];
  return {
    media: value['media'],
    name: typeof value['name'] === 'string' ? value['name'] : '',
    box:
      isRecord(box) && positive(box['length']) && positive(box['height']) && positive(box['width'])
        ? { length: box['length'], height: box['height'], width: box['width'] }
        : undefined,
    forwardYaw: typeof yaw === 'number' && Number.isFinite(yaw) ? yaw : 0,
  };
}

/** Redeems the ticket, uncached. Used, expired and never issued are one 404. */
export async function fetchModelViewer(
  ticket: string,
  fetchImpl: typeof fetch = (input, init) => fetch(input, init),
): Promise<ModelViewerOutcome> {
  let response: Response;
  try {
    response = await fetchImpl(`/v1/model-viewer/${encodeURIComponent(ticket)}`, {
      cache: 'no-store',
    });
  } catch {
    return { kind: 'failed', detail: 'geen verbinding met de server' };
  }
  if (response.status === 404) return { kind: 'gone' };
  if (!response.ok) return { kind: 'failed', detail: `fout ${response.status}` };
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return { kind: 'failed', detail: 'onleesbaar antwoord' };
  }
  if (!isRecord(body)) return { kind: 'failed', detail: 'onvolledig antwoord' };
  const model = viewerModelOf(body['model']);
  const origin = body['consoleOrigin'];
  const media = isRecord(body['media']) ? body['media'] : {};
  if (!model || typeof origin !== 'string' || !isOrigin(origin)) {
    return { kind: 'failed', detail: 'onvolledig antwoord' };
  }
  return {
    kind: 'ready',
    answer: {
      model,
      clips: Array.isArray(body['clips'])
        ? body['clips'].filter((clip): clip is string => typeof clip === 'string')
        : [],
      media: Object.fromEntries(
        Object.entries(media).filter(
          (entry): entry is [string, string] => typeof entry[1] === 'string',
        ),
      ),
      consoleOrigin: origin,
    },
  };
}

/** What the viewer needs drawn (render/models/viewer-scene.ts; a fake in tests). */
export type ModelViewerRenderer = {
  /** The GLB fetched and parsed, shown beside the figure; the clips it has. */
  load(answer: ModelViewerAnswer): Promise<string[]>;
  /** A clip by its name, looping; null: the bind pose. */
  play(clip: string | null): void;
  /** The thumbnail, 640 × 480, as a JPEG: the model alone in its current pose, framed (ADR-060). */
  capture(): Promise<Blob>;
};

export type ModelViewerMessage =
  | { type: 'ready'; clips: string[] }
  | { type: 'trouble'; text: string }
  | { type: 'thumbnail'; jpeg: Blob };

/**
 * The viewer's side of the conversation: loads the model, says `ready` with
 * its clips, and answers what the console asks -- only the console the ticket
 * names, one request at a time.
 */
export class ModelViewerHost {
  readonly #answer: ModelViewerAnswer;
  readonly #renderer: ModelViewerRenderer;
  readonly #post: (message: ModelViewerMessage) => void;
  #clips: string[] = [];
  #queue: Promise<void> = Promise.resolve();

  constructor(input: {
    answer: ModelViewerAnswer;
    renderer: ModelViewerRenderer;
    /** Posts to the console, at its origin only. */
    post: (message: ModelViewerMessage) => void;
  }) {
    this.#answer = input.answer;
    this.#renderer = input.renderer;
    this.#post = input.post;
  }

  async start(): Promise<void> {
    try {
      this.#clips = await this.#renderer.load(this.#answer);
    } catch {
      this.#post({ type: 'trouble', text: MODEL_VIEWER_TEXT.failed });
      return;
    }
    this.#post({ type: 'ready', clips: [...this.#clips] });
  }

  /** A message from the window: acted on only from the console's origin. */
  receive(event: { origin: string; data: unknown }): Promise<void> {
    if (event.origin !== this.#answer.consoleOrigin) return Promise.resolve();
    const data = event.data;
    if (!isRecord(data)) return Promise.resolve();
    this.#queue = this.#queue
      .then(() => this.#handle(data))
      .catch(() => {
        this.#post({ type: 'trouble', text: MODEL_VIEWER_TEXT.drawing });
      });
    return this.#queue;
  }

  async #handle(message: Record<string, unknown>): Promise<void> {
    switch (message['type']) {
      case 'play': {
        const clip = message['clip'];
        if (clip === null) {
          this.#renderer.play(null);
          return;
        }
        if (typeof clip !== 'string' || !this.#clips.includes(clip)) {
          this.#post({ type: 'trouble', text: MODEL_VIEWER_TEXT.noClip });
          return;
        }
        this.#renderer.play(clip);
        return;
      }
      case 'capture':
        this.#post({ type: 'thumbnail', jpeg: await this.#renderer.capture() });
        return;
      default:
        return;
    }
  }
}
